import { CarouselProps } from '@/components/ui/custom/Carousel';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { CourseCard } from '@/components/ui/custom/coursePath/card/coursePathCard/CoursePathCard';
import Text from '@/components/ui/custom/dataDisplay/Text';
import InfinityList from '@/components/ui/custom/InfinityList';
import { ListCourseProgressFilters } from '@/data/services/courseProgressServices';
import { SimplifiedCourseProgress } from '@/models/StudentCourseProgress';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProgressStatusEnum } from '@/enums/ProgressStatusEnum';
import CourseProgressCardFactory from './card/CourseProgressCardFactory';
import CourseProgressSectionHeader from './CourseProgressSectionHeader';

export type CourseProgressCarouselProps = {
  section: ProgressStatusEnum;
  filters?: ListCourseProgressFilters;
  onMutate?: (courseProgress: SimplifiedCourseProgress) => void;
  onFilter?: (filters: ListCourseProgressFilters) => void;
  route?: string;
  courseProgresses?: SimplifiedCourseProgress[];
  isLoading?: boolean;
  error: unknown;
} & Omit<CarouselProps, 'children'>;

export default function CourseProgressCarousel({
  section,
  filters,
  route,
  error,
  onFilter,
  courseProgresses = [],
  isLoading,
  ...carouselProps
}: CourseProgressCarouselProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'coursesView.student',
  });

  const errorDetail = getErrorMessage(error);

  const divClassName = 'flex w-full h-[256px] justify-center items-center';

  const loadingCards = useMemo(
    () =>
      Array.from({ length: 20 }).map((_, index) => (
        <CourseCard key={index} loading />
      )),
    [],
  );

  return (
    <div className="flex flex-col bg-accent-content/30 rounded-lg border border-neutral-content py-2.5 pl-2.5">
      <CourseProgressSectionHeader
        section={section}
        filters={filters}
        onFilter={onFilter}
      />
      <InfinityList
        {...carouselProps}
        className="w-full flex-row flex-wrap gap-2.5 py-2"
      >
        <ConditionalRenderer condition={isLoading}>
          {loadingCards}
        </ConditionalRenderer>

        <ConditionalRenderer condition={errorDetail}>
          <div className={divClassName}>
            <Text
              format="rubik-500"
              text={errorDetail}
              className="text-accent"
            />
          </div>
        </ConditionalRenderer>

        <ConditionalRenderer condition={courseProgresses.length}>
          {courseProgresses.map(courseProgress => (
            <CourseProgressCardFactory
              key={courseProgress.id}
              courseProgress={courseProgress}
            />
          ))}
        </ConditionalRenderer>

        <ConditionalRenderer
          condition={!isLoading && !errorDetail && !courseProgresses.length}
        >
          <div className="flex w-full h-[256px] justify-center items-center">
            <Text text={t('noCourseProgressesFound')} />
          </div>
        </ConditionalRenderer>
      </InfinityList>
    </div>
  );
}
